import { createWebHistory, type RouteRecordRaw } from "vue-router";

import { createRouter } from "@tager/admin-layout";
import { PAGE_FORM_ROUTE, PAGE_LIST_ROUTE } from "@tager/admin-pages";
import { createMenuRoute } from "@tager/admin-menus";
import {
  BLOG_CATEGORY_FORM_ROUTE,
  BLOG_CATEGORY_LIST_ROUTE,
  BLOG_POST_FORM_ROUTE,
  BLOG_POST_LIST_ROUTE,
} from "@tager/admin-blog";
import {
  SETTINGS_ITEM_FORM_ROUTE,
  SETTINGS_ITEM_LIST_ROUTE,
} from "@tager/admin-settings";
import { SEO_SETTINGS_ROUTE, SEO_TEMPLATES_ROUTE } from "@tager/admin-seo";
import {
  ADMIN_FORM_ROUTE,
  ADMIN_LIST_ROUTE,
  ROLE_FORM_ROUTE,
  ROLE_LIST_ROUTE,
} from "@tager/admin-administrators";
import {
  EMAIL_LOG_LIST_ROUTE,
  EMAIL_TEMPLATE_FORM_ROUTE,
  EMAIL_TEMPLATE_LIST_ROUTE,
} from "@tager/admin-mail";
import { EXPORT_FORM_ROUTE, EXPORT_LIST_ROUTE } from "@tager/admin-export";
import { IMPORT_FORM_ROUTE, IMPORT_LIST_ROUTE } from "@tager/admin-import";
import { FILES_FORM_ROUTE, FILES_LIST_ROUTE } from "@tager/admin-files";

import Home from "@/views/Home.vue";
import { LINKS } from "@/constants/links";
import TermList from "@/views/TermList";
import { ROUTE_PATHS } from "@/constants/paths";
import TermForm from "@/views/TermForm";
import TermLinkList from "@/views/TermLinkList/TermLinkList.vue";
import TermLinkForm from "@/views/TermLinkForm/TermLinkForm.vue";
import ProductTypeList from "@/views/ProductTypeList";
import ProductTypeForm from "@/views/ProductTypeForm";
import BrandList from "@/views/BrandList";
import BrandForm from "@/views/BrandForm";
import FieldList from "@/views/FieldList";
import FieldForm from "@/views/FieldForm";
import CategoryList from "@/views/CategoryList";
import CategoryForm from "@/views/CategoryForm";
import ProductList from "@/views/ProductList";
import ProductForm from "@/views/ProductForm";
import CollectionList from "@/views/CollectionList";
import CollectionForm from "@/views/CollectionForm";
import SpecialList from "@/views/SpecialList";
import SpecialForm from "@/views/SpecialForm";
import ProductGroupList from "@/views/ProductGroupList";
import ProductGroupForm from "@/views/ProductGroupForm";
import SeoList from "@/views/SeoList";
import BasisProductList from "@/views/BasisProductList";
import BasisProductForm from "@/views/BasisProductForm";
import CategoryTree from "@/views/CategoryTree";

export const HOME_ROUTE: RouteRecordRaw = {
  path: "/",
  component: Home,
  name: "Home",
  meta: {
    getBreadcrumbs: () => [LINKS.HOME],
  },
};

export const SPECIAL_LIST_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.SPECIAL_LIST,
  component: SpecialList,
  name: "Спецпредложения",
  meta: {
    getBreadcrumbs: () => [LINKS.HOME, LINKS.SPECIAL_LIST],
  },
};

export const SPECIAL_FORM_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.SPECIAL_FORM,
  component: SpecialForm,
  name: "Форма спецпредложения",
  meta: {
    getBreadcrumbs: (route) => [
      LINKS.HOME,
      LINKS.SPECIAL_LIST,
      {
        url: route.path,
        text: "Форма спецпредложения",
      },
    ],
  },
};

export const SEO_LIST_CATEGORY_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.SEO_LIST_CATEGORY,
  component: SeoList,
  name: "SEO Поля",
  meta: {
    getBreadcrumbs: () => [LINKS.HOME, LINKS.SEO_LIST_CATEGORY],
  },
};

export const CATEGORY_LIST_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.CATEGORY_LIST,
  component: CategoryList,
  name: "Категории",
  meta: {
    getBreadcrumbs: () => [LINKS.HOME, LINKS.CATEGORY_LIST],
  },
};

export const CATEGORY_TREE_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.CATEGORY_TREE,
  component: CategoryTree,
  name: "Дерево категорий",
  meta: {
    getBreadcrumbs: () => [LINKS.HOME, LINKS.CATEGORY_TREE],
  },
};

export const CATEGORY_FORM_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.CATEGORY_FORM,
  component: CategoryForm,
  name: "Форма категории",
  meta: {
    getBreadcrumbs: (route) => [
      LINKS.HOME,
      LINKS.CATEGORY_LIST,
      {
        url: route.path,
        text: "Форма категории",
      },
    ],
  },
};

export const BRAND_LIST_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.BRAND_LIST,
  component: BrandList,
  name: "Производители",
  meta: {
    getBreadcrumbs: () => [LINKS.HOME, LINKS.BRANDS_LIST],
  },
};

export const BRAND_FORM_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.BRAND_FORM,
  component: BrandForm,
  name: "Форма производителя",
  meta: {
    getBreadcrumbs: (route) => [
      LINKS.HOME,
      LINKS.BRANDS_LIST,
      {
        url: route.path,
        text: "Форма производителя",
      },
    ],
  },
};

export const COLLECTION_LIST_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.COLLECTION_LIST,
  component: CollectionList,
  name: "Коллекции",
  meta: {
    getBreadcrumbs: () => [LINKS.HOME, LINKS.COLLECTION_LIST],
  },
};

export const COLLECTION_FORM_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.COLLECTION_FORM,
  component: CollectionForm,
  name: "Форма коллекции",
  meta: {
    getBreadcrumbs: (route) => [
      LINKS.HOME,
      LINKS.COLLECTION_LIST,
      {
        url: route.path,
        text: "Форма коллекции",
      },
    ],
  },
};

export const BASIS_PRODUCT_LIST_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.BASIS_PRODUCT_LIST,
  component: BasisProductList,
  name: "Базовые товары",
  meta: {
    getBreadcrumbs: () => [LINKS.HOME, LINKS.BASIS_PRODUCT_LIST],
  },
};

export const BASIS_PRODUCT_FORM_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.BASIS_PRODUCT_FORM,
  component: BasisProductForm,
  name: "Базисный товар",
  meta: {
    getBreadcrumbs: (route) => [
      LINKS.HOME,
      LINKS.BASIS_PRODUCT_LIST,
      {
        url: route.path,
        text: "Базисный товар",
      },
    ],
  },
};

export const PRODUCT_LIST_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.PRODUCT_LIST,
  component: ProductList,
  name: "Товары",
  meta: {
    getBreadcrumbs: () => [LINKS.HOME, LINKS.PRODUCT_LIST],
  },
};

export const PRODUCT_FORM_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.PRODUCT_FORM,
  component: ProductForm,
  name: "Форма товара",
  meta: {
    getBreadcrumbs: (route) => [
      LINKS.HOME,
      LINKS.PRODUCT_LIST,
      {
        url: route.path,
        text: "Форма товара",
      },
    ],
  },
};

export const PRODUCT_TYPE_LIST_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.PRODUCT_TYPE_LIST,
  component: ProductTypeList,
  name: "Типы товаров",
  meta: {
    getBreadcrumbs: () => [LINKS.HOME, LINKS.PRODUCT_TYPE_LIST],
  },
};

export const PRODUCT_TYPE_FORM_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.PRODUCT_TYPE_FORM,
  component: ProductTypeForm,
  name: "Форма типа товара",
  meta: {
    getBreadcrumbs: (route) => [
      LINKS.HOME,
      LINKS.PRODUCT_TYPE_LIST,
      {
        url: route.path,
        text: "Форма типа товара",
      },
    ],
  },
};

export const PRODUCT_GROUP_LIST_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.PRODUCT_GROUP_LIST,
  component: ProductGroupList,
  name: "Группы товаров",
  meta: {
    getBreadcrumbs: () => [LINKS.HOME, LINKS.PRODUCT_GROUP_LIST],
  },
};

export const PRODUCT_GROUP_FORM_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.PRODUCT_GROUP_FORM,
  component: ProductGroupForm,
  name: "Форма груп товаров",
  meta: {
    getBreadcrumbs: (route) => [
      LINKS.HOME,
      LINKS.PRODUCT_GROUP_LIST,
      {
        url: route.path,
        text: "Форма груп товаров",
      },
    ],
  },
};

export const FIELD_LIST_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.FIELD_LIST,
  component: FieldList,
  name: "Поля",
  meta: {
    getBreadcrumbs: (route) => [
      LINKS.HOME,
      LINKS.PRODUCT_TYPE_LIST,
      { url: route.path, text: "Поля типа товаров" },
    ],
  },
};

export const FIELD_CREATE_FORM_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.FIELD_CREATE_FORM,
  component: FieldForm,
  name: "Форма поля",
  meta: {
    getBreadcrumbs: (route) => [
      LINKS.HOME,
      LINKS.PRODUCT_TYPE_LIST,
      { url: route.path, text: "Поля" },
      {
        url: route.path,
        text: "Форма поля",
      },
    ],
  },
};

export const FIELD_EDIT_FORM_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.FIELD_EDIT_FORM,
  component: FieldForm,
  name: "Редактирование поля",
  meta: {
    getBreadcrumbs: (route) => [
      LINKS.HOME,
      LINKS.PRODUCT_TYPE_LIST,
      { url: route.path, text: "Поля" },
      {
        url: route.path,
        text: "Форма поля",
      },
    ],
  },
};

export const TERM_LIST_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.TERM_LIST,
  component: TermList,
  name: "TermList",
  meta: {
    getBreadcrumbs: () => [LINKS.HOME, LINKS.TERM],
  },
};
export const TERM_FORM_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.TERM_FORM,
  component: TermForm,
  name: "TermForm",
  meta: {
    getBreadcrumbs: (route) => [
      LINKS.HOME,
      LINKS.TERM,
      {
        url: route.path,
        text: "Форма для слова",
      },
    ],
  },
};

export const TERM_LINK_LIST_ROUTE: RouteRecordRaw = {
  path: ROUTE_PATHS.TERM_LINK_LIST,
  component: TermLinkList,
  name: "TermLinkList",
  meta: {
    getBreadcrumbs: () => [LINKS.HOME, LINKS.TERM],
  },
};

export const TERM_LINK_FORM_ROUTE_CREATE: RouteRecordRaw = {
  path: ROUTE_PATHS.TERM_LINK_FORM_CREATE,
  component: TermLinkForm,
  name: "TermLinkForm",
  meta: {
    getBreadcrumbs: (route) => [
      LINKS.HOME,
      LINKS.TERM,
      {
        url: route.path,
        text: "Форма Ссылки",
      },
    ],
  },
};

export const TERM_LINK_FORM_ROUTE_UPDATE: RouteRecordRaw = {
  path: ROUTE_PATHS.TERM_LINK_FORM_UPDATE,
  component: TermLinkForm,
  name: "TermLinkForm",
  meta: {
    getBreadcrumbs: (route) => [
      LINKS.HOME,
      LINKS.TERM,
      {
        url: route.path,
        text: "Форма Ссылки",
      },
    ],
  },
};

export const router = createRouter({
  history: createWebHistory("/admin"),
  routes: [
    HOME_ROUTE,
    PAGE_LIST_ROUTE,
    PAGE_FORM_ROUTE,
    createMenuRoute("Меню"),
    BLOG_POST_LIST_ROUTE,
    BLOG_POST_FORM_ROUTE,
    BLOG_CATEGORY_LIST_ROUTE,
    BLOG_CATEGORY_FORM_ROUTE,
    SPECIAL_LIST_ROUTE,
    SPECIAL_FORM_ROUTE,
    SEO_LIST_CATEGORY_ROUTE,
    CATEGORY_TREE_ROUTE,
    CATEGORY_LIST_ROUTE,
    CATEGORY_FORM_ROUTE,
    BRAND_LIST_ROUTE,
    COLLECTION_LIST_ROUTE,
    COLLECTION_FORM_ROUTE,
    BRAND_FORM_ROUTE,
    BASIS_PRODUCT_LIST_ROUTE,
    BASIS_PRODUCT_FORM_ROUTE,
    PRODUCT_LIST_ROUTE,
    PRODUCT_FORM_ROUTE,
    PRODUCT_TYPE_LIST_ROUTE,
    PRODUCT_TYPE_FORM_ROUTE,
    PRODUCT_GROUP_LIST_ROUTE,
    PRODUCT_GROUP_FORM_ROUTE,

    FIELD_LIST_ROUTE,
    FIELD_EDIT_FORM_ROUTE,
    FIELD_CREATE_FORM_ROUTE,

    TERM_LIST_ROUTE,
    TERM_FORM_ROUTE,
    TERM_LINK_LIST_ROUTE,
    TERM_LINK_FORM_ROUTE_CREATE,
    TERM_LINK_FORM_ROUTE_UPDATE,
    EMAIL_TEMPLATE_LIST_ROUTE,
    EMAIL_TEMPLATE_FORM_ROUTE,
    EMAIL_LOG_LIST_ROUTE,
    SETTINGS_ITEM_LIST_ROUTE,
    SETTINGS_ITEM_FORM_ROUTE,
    SEO_SETTINGS_ROUTE,
    ROLE_LIST_ROUTE,
    ROLE_FORM_ROUTE,
    ADMIN_LIST_ROUTE,
    ADMIN_FORM_ROUTE,
    SEO_TEMPLATES_ROUTE,
    EXPORT_LIST_ROUTE,
    EXPORT_FORM_ROUTE,
    IMPORT_LIST_ROUTE,
    IMPORT_FORM_ROUTE,
    FILES_LIST_ROUTE,
    FILES_FORM_ROUTE,
  ],
});

export default router;
